import React, { useEffect, useRef, useState } from "react";
import { ChevronLeft, Loader2, MapPin } from "lucide-react";
import { countries } from "../utils/countries";

const LOC_CACHE_KEY   = "omah_location";
const LOC_CACHE_AT    = "omah_location_at";
const ONE_DAY_MS      = 24 * 60 * 60 * 1000;

/**
 * LocationPicker
 * --------------
 * Smart location selector that:
 *  1. Auto-detects the user's country+region via `ipapi.co` (free, no API key)
 *     — falls back to the `omah_detected_country` code already cached by App.jsx
 *  2. Shows a two-step dropdown: pick a country → pick a state/region
 *     (states fetched from countriesnow.space, cached in sessionStorage)
 *  3. Persists the selection to localStorage so the next visit remembers it
 *
 * Props:
 *  triggerClassName  – extra classes for the trigger button (desktop vs mobile)
 *  dropdownAlign     – "left" | "right" (default "left") for panel positioning
 */
const LocationPicker = ({ triggerClassName = "", dropdownAlign = "left" }) => {
  /* ── state ── */
  const [isOpen,          setIsOpen]          = useState(false);
  const [view,            setView]            = useState("countries"); // "countries" | "states"
  const [selectedCountry, setSelectedCountry] = useState(null);        // { name, code }
  const [selectedRegion,  setSelectedRegion]  = useState("");
  const [stateList,       setStateList]       = useState([]);
  const [loadingStates,   setLoadingStates]   = useState(false);
  const [searchQuery,     setSearchQuery]     = useState("");
  const [detecting,       setDetecting]       = useState(false);

  const containerRef = useRef(null);
  const searchRef    = useRef(null);

  /* ── close on outside click ── */
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (!containerRef.current?.contains(e.target)) {
        setIsOpen(false);
        setSearchQuery("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [isOpen]);

  /* ── focus search input when dropdown opens ── */
  useEffect(() => {
    if (isOpen) setTimeout(() => searchRef.current?.focus(), 50);
  }, [isOpen, view]);

  /* ── on mount: load saved → detect ── */
  useEffect(() => {
    const saved = localStorage.getItem(LOC_CACHE_KEY);
    if (saved) {
      try {
        const { country, region } = JSON.parse(saved);
        if (country) { setSelectedCountry(country); setSelectedRegion(region || ""); return; }
      } catch {}
    }
    detectLocation();
  }, []);

  /* ── IP-based detection ── */
  const detectLocation = async () => {
    // First try the country code already stored by App.jsx
    const cachedCode = localStorage.getItem("omah_detected_country");
    const cachedAt   = localStorage.getItem(LOC_CACHE_AT);
    const fresh      = cachedAt && Date.now() - parseInt(cachedAt, 10) < ONE_DAY_MS;

    if (cachedCode && fresh) {
      const match = countries.find((c) => c.code === cachedCode);
      if (match) { setSelectedCountry(match); return; }
    }

    // Fallback: ipapi.co (free, HTTPS, ~1 000 req/day per IP)
    setDetecting(true);
    try {
      const res  = await fetch("https://ipapi.co/json/");
      const data = await res.json();
      if (data.country_code) {
        const match = countries.find((c) => c.code === data.country_code);
        if (match) {
          const region = data.region || "";
          setSelectedCountry(match);
          setSelectedRegion(region);
          persistLocation(match, region);
        }
      }
    } catch (_) {}
    setDetecting(false);
  };

  const persistLocation = (country, region) => {
    localStorage.setItem(LOC_CACHE_KEY,  JSON.stringify({ country, region }));
    localStorage.setItem(LOC_CACHE_AT,   String(Date.now()));
  };

  /* ── fetch states for a country ── */
  const fetchStates = async (countryName) => {
    const key    = `omah_states_${countryName}`;
    const cached = sessionStorage.getItem(key);
    if (cached) { setStateList(JSON.parse(cached)); setLoadingStates(false); return; }

    setLoadingStates(true);
    try {
      const res  = await fetch("https://countriesnow.space/api/v0.1/countries/states", {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ country: countryName }),
      });
      const data = await res.json();
      if (!data.error && Array.isArray(data.data?.states)) {
        const names = data.data.states.map((s) => s.name).filter(Boolean);
        sessionStorage.setItem(key, JSON.stringify(names));
        setStateList(names);
      } else {
        setStateList([]);
      }
    } catch (_) {
      setStateList([]);
    }
    setLoadingStates(false);
  };

  /* ── handlers ── */
  const handleToggle = () => {
    setIsOpen((o) => !o);
    setView("countries");
    setSearchQuery("");
  };

  const handleSelectCountry = (country) => {
    setSelectedCountry(country);
    setSelectedRegion("");
    persistLocation(country, "");
    setStateList([]);
    setSearchQuery("");
    setView("states");
    fetchStates(country.name);
  };

  const handleSelectRegion = (region) => {
    setSelectedRegion(region);
    persistLocation(selectedCountry, region);
    setIsOpen(false);
    setView("countries");
    setSearchQuery("");
  };

  const handleConfirmCountryOnly = () => {
    persistLocation(selectedCountry, "");
    setIsOpen(false);
    setView("countries");
    setSearchQuery("");
  };

  const handleBack = () => {
    setView("countries");
    setSearchQuery("");
  };

  /* ── derived display ── */
  const displayLabel = (() => {
    if (detecting) return "Detecting…";
    const place = selectedRegion || selectedCountry?.name || "Location";
    return place.length > 14 ? place.slice(0, 13) + "…" : place;
  })();

  const displayCode = selectedCountry?.code || "";

  /* ── filtered lists ── */
  const q           = searchQuery.toLowerCase().trim();
  const filteredCountries = q
    ? countries.filter((c) => c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q))
    : countries;
  const filteredStates = q
    ? stateList.filter((s) => s.toLowerCase().includes(q))
    : stateList;

  /* ── dropdown alignment ── */
  const panelClass = dropdownAlign === "right"
    ? "right-0"
    : "left-0";

  /* ── render ── */
  return (
    <div className="relative shrink-0" ref={containerRef}>

      {/* ── Trigger ── */}
      <button
        type="button"
        onClick={handleToggle}
        className={`flex items-center gap-1.5 text-sm font-medium text-gray-800 transition-colors whitespace-nowrap ${triggerClassName}`}
      >
        {detecting
          ? <Loader2 size={13} className="text-gray-400 animate-spin shrink-0" />
          : <MapPin   size={14} className="text-gray-500 shrink-0" />
        }
        <span className="leading-none">{displayLabel}</span>
        {displayCode && !detecting && (
          <span className="text-[10px] text-gray-400 font-normal leading-none">·{displayCode}</span>
        )}
      </button>

      {/* ── Dropdown panel ── */}
      {isOpen && (
        <div
          className={`absolute top-[calc(100%+6px)] ${panelClass} z-[200] w-[248px] bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden`}
          style={{ maxHeight: "calc(100dvh - 120px)" }}
        >

          {/* Panel header */}
          <div className="flex items-center gap-2 px-3 py-2.5 border-b border-gray-100 bg-gray-50/80">
            {view === "states" && (
              <button
                onClick={handleBack}
                className="flex items-center justify-center w-6 h-6 rounded-md hover:bg-gray-200 text-gray-500 transition-colors"
              >
                <ChevronLeft size={15} />
              </button>
            )}
            <span className="text-xs font-semibold text-gray-500 flex-1 truncate">
              {view === "states"
                ? `📍 ${selectedCountry?.name} — pick a state`
                : "📍 Select your location"}
            </span>
          </div>

          {/* Search input */}
          <div className="px-3 pt-2.5 pb-2 border-b border-gray-100">
            <input
              ref={searchRef}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={view === "states" ? "Filter states…" : "Search country…"}
              className="w-full text-sm py-1.5 px-2.5 border border-gray-200 rounded-lg
                         bg-white focus:outline-none focus:border-green-400 focus:ring-1 focus:ring-green-100
                         placeholder:text-gray-400"
            />
          </div>

          {/* List */}
          <div className="overflow-y-auto" style={{ maxHeight: "260px" }}>

            {/* ── Country view ── */}
            {view === "countries" && filteredCountries.map((c) => (
              <button
                key={c.code}
                onClick={() => handleSelectCountry(c)}
                className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 transition-colors
                  ${selectedCountry?.code === c.code
                    ? "bg-green-50 text-green-800"
                    : "text-gray-700 hover:bg-gray-50"}`}
              >
                <span className="text-[11px] font-mono text-gray-400 w-[24px] shrink-0">{c.code}</span>
                <span className="flex-1 truncate">{c.name}</span>
                {selectedCountry?.code === c.code && (
                  <span className="text-green-500 text-xs shrink-0">✓</span>
                )}
              </button>
            ))}
            {view === "countries" && filteredCountries.length === 0 && (
              <p className="text-xs text-gray-400 text-center py-5">No countries match "{searchQuery}"</p>
            )}

            {/* ── States view ── */}
            {view === "states" && (
              <>
                {/* "No specific state" shortcut */}
                <button
                  onClick={handleConfirmCountryOnly}
                  className="w-full text-left px-3 py-2 text-sm text-gray-500 hover:bg-gray-50 transition-colors italic border-b border-gray-100"
                >
                  All of {selectedCountry?.name}
                </button>

                {loadingStates && (
                  <div className="flex items-center justify-center gap-2 py-6 text-xs text-gray-400">
                    <Loader2 size={14} className="animate-spin" /> Loading regions…
                  </div>
                )}

                {!loadingStates && filteredStates.map((s) => (
                  <button
                    key={s}
                    onClick={() => handleSelectRegion(s)}
                    className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 transition-colors
                      ${selectedRegion === s
                        ? "bg-green-50 text-green-800 font-medium"
                        : "text-gray-700 hover:bg-gray-50"}`}
                  >
                    <span className="flex-1 truncate">{s}</span>
                    {selectedRegion === s && <span className="text-green-500 text-xs shrink-0">✓</span>}
                  </button>
                ))}

                {!loadingStates && stateList.length === 0 && (
                  <p className="text-xs text-gray-400 text-center py-4">
                    No regions available — using country level
                  </p>
                )}

                {!loadingStates && stateList.length > 0 && filteredStates.length === 0 && (
                  <p className="text-xs text-gray-400 text-center py-4">No match for "{searchQuery}"</p>
                )}
              </>
            )}
          </div>

        </div>
      )}

    </div>
  );
};

export default LocationPicker;
